import { useParams, Link } from "react-router-dom";
import { MapPin, ArrowUpRight, Check } from "lucide-react";
import { LOCATIONS } from "../lib/locations";
import { PageHeader } from "../components/PageHeader";
import { Reveal, MaskText, Line } from "../components/Motion";
import { EditorialMarquee } from "../sections/EditorialMarquee";
import { useSEO } from "../lib/useSEO";

const SITE = "https://jasonhowardphotography.co.uk";

export default function Location({ areaSlug }) {
  const params = useParams();
  const area = areaSlug || params.area;
  const loc = LOCATIONS.find((l) => l.slug === area);

  const jsonLd = loc
    ? [
        {
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Wedding photography",
          areaServed: { "@type": "Place", name: loc.area },
          provider: { "@type": "LocalBusiness", name: "Jason Howard Photography", "@id": `${SITE}/#business` },
          name: `Wedding Photographer ${loc.area}`,
          url: `${SITE}/${loc.slug}-wedding-photographer/`,
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: `Wedding Photographer ${loc.area}`, item: `${SITE}/${loc.slug}-wedding-photographer/` },
          ],
        },
      ]
    : undefined;

  useSEO({
    title: loc ? `Wedding Photographer ${loc.area} | Jason Howard Photography` : "Wedding Photographer | Jason Howard Photography",
    description: loc
      ? `${loc.lede} Natural, relaxed and unobtrusive ${loc.area} wedding photography from Jason Howard — 20+ years and 1,600+ weddings. Check availability.`
      : "Wedding photography across the North West and North Wales.",
    canonical: loc ? `${SITE}/${loc.slug}-wedding-photographer/` : undefined,
    image: loc ? `${SITE}${loc.hero}` : undefined,
    jsonLd,
  });

  if (!loc) {
    return (
      <main className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6">
        <p className="font-serif text-5xl text-ink mb-6">Area not found</p>
        <Link to="/contact/" className="text-[11px] uppercase tracking-[0.2em] link-underline">Get in touch</Link>
      </main>
    );
  }

  // On direct loads, index.html paints a static header+hero (#jh-loc-hero) before JS —
  // skip re-rendering them so the static paint stays the LCP. On client-side
  // navigation the static block is absent, so render normally.
  const staticHdr =
    typeof document !== "undefined" &&
    document.getElementById("jh-loc-hero")?.dataset.area === loc.slug;

  return (
    <>
      {!staticHdr && (
        <>
          <PageHeader eyebrow={`Wedding Photographer · ${loc.area}`} lines={[`Weddings in`, loc.area + "."]} intro={loc.lede} />

          <section className="pb-16 md:pb-24">
            <div className="max-w-[1500px] mx-auto px-5 md:px-10">
              <div className="relative overflow-hidden aspect-[16/9] bg-ink/5">
                <img
                  src={loc.hero}
                  srcSet={`${loc.hero.replace("/jhp/", "/jhp/800/")} 800w, ${loc.hero} 1600w`}
                  sizes="100vw"
                  alt={loc.heroAlt}
                  fetchPriority="high"
                  decoding="async"
                  width="1600"
                  height="833"
                  className="img-cover"
                />
              </div>
            </div>
          </section>
        </>
      )}

      <section className="pb-20 md:pb-28">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-7">
            <p className="text-ink text-xl md:text-2xl font-serif leading-relaxed mb-8">{loc.intro}</p>
            <div className="space-y-5 text-ink-soft leading-relaxed">
              {loc.body.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact/" data-testid="location-cta" className="inline-flex items-center bg-ink text-paper text-[11px] uppercase tracking-[0.2em] px-8 py-4 rounded-full hover:bg-sand-deep transition-colors duration-500">Check Availability</Link>
              <Link to="/gallery/portfolio/" className="inline-flex items-center border border-ink text-ink text-[11px] uppercase tracking-[0.2em] px-8 py-4 rounded-full hover:bg-ink hover:text-paper transition-colors duration-500">View Portfolio</Link>
            </div>
          </div>

          <div className="md:col-span-4 md:col-start-9">
            <p className="text-sand-deep text-[11px] uppercase tracking-[0.3em] mb-5 flex items-center gap-2"><MapPin size={14} /> Venues I love in {loc.area}</p>
            <ul className="space-y-3">
              {loc.venues.map((v) => (
                <li key={v} className="flex items-start gap-3 text-ink-soft"><Check size={15} className="mt-1 text-sand-deep shrink-0" /> {v}</li>
              ))}
            </ul>
            {loc.stories.length > 0 && (
              <>
                <Line className="my-8" />
                <p className="text-sand-deep text-[11px] uppercase tracking-[0.3em] mb-4">Recent {loc.area} weddings</p>
                <ul className="space-y-3">
                  {loc.stories.map((s) => (
                    <li key={s.to}>
                      <Link to={s.to} className="group inline-flex items-center gap-2 text-ink link-underline">
                        {s.label} <ArrowUpRight size={15} className="text-sand-deep" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-36">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 border-t border-ink/10 pt-14">
          <MaskText as="h2" className="font-serif text-2xl md:text-4xl text-ink mb-6" lines={["Also covering nearby"]} />
          <div className="flex flex-wrap gap-3">
            {LOCATIONS.filter((l) => l.slug !== loc.slug).map((l) => (
              <Link key={l.slug} to={`/${l.slug}-wedding-photographer/`} data-testid={`area-link-${l.slug}`} className="text-[11px] uppercase tracking-[0.15em] border border-ink/20 text-ink-soft hover:border-ink hover:text-ink rounded-full px-5 py-2.5 transition-colors duration-300">
                {l.area}
              </Link>
            ))}
          </div>
        </div>
      </section>
      <EditorialMarquee />
    </>
  );
}
